import { useState } from 'react'
import { ApiError } from '@/api/errors'
import type { OrderStageView, StageStatus } from '@/api/types'
import { useUser } from '@/auth/useAuth'
import { isControl, isAdmin } from '@/auth/rbac'
import { stageStatusLabel } from '@/i18n/labels'
import { useDelayReasons } from '@/features/refs/queries'
import { Button, Field, Select, Sheet, Textarea } from '@/ui'
import { usePauseStage, useStageCommand, type StageCommand } from './queries'

interface Cmd {
  command: StageCommand
  label: string
  variant?: 'primary' | 'danger'
}

function commandsFor(status: StageStatus, isOptional: boolean, control: boolean): Cmd[] {
  const list: Cmd[] = []
  switch (status) {
    case 'AVAILABLE':
      list.push({ command: 'accept', label: 'Принять' })
      break
    case 'ACCEPTED':
      list.push({ command: 'start', label: 'Начать' })
      break
    case 'IN_PROGRESS':
      list.push({ command: 'complete', label: 'Завершить' })
      break
    case 'PAUSED':
      list.push({ command: 'resume', label: 'Возобновить' })
      break
    case 'COMPLETED':
      list.push({ command: 'transfer', label: 'Передать далее' })
      break
  }
  if (isOptional && control && (status === 'AVAILABLE' || status === 'ACCEPTED')) {
    list.push({ command: 'skip', label: 'Пропустить', variant: 'danger' })
  }
  return list
}

export function StageActionSheet({
  orderId,
  stage,
  open,
  onClose,
}: {
  orderId: string
  stage: OrderStageView
  open: boolean
  onClose: () => void
}) {
  const user = useUser()
  const control = isControl(user.role)
  const command = useStageCommand(orderId)
  const pause = usePauseStage(orderId)
  const delayReasons = useDelayReasons()

  const [comment, setComment] = useState('')
  const [delayReasonId, setDelayReasonId] = useState('')

  const commands = commandsFor(stage.status, stage.is_optional, control)
  const showPause = stage.status === 'IN_PROGRESS'
  const activeReasons = (delayReasons.data?.items ?? []).filter((r) => r.is_active)

  const busyError = command.error ?? pause.error
  const errMessage = busyError instanceof ApiError ? busyError.message : null
  const busy = command.isPending || pause.isPending

  function run(c: StageCommand) {
    command.mutate(
      { stageId: stage.id, command: c, version: stage.version, reason: comment.trim() || undefined },
      { onSuccess: onClose },
    )
  }

  function runPause() {
    if (!delayReasonId) return
    pause.mutate(
      { stageId: stage.id, version: stage.version, delay_reason_id: delayReasonId },
      { onSuccess: onClose },
    )
  }

  return (
    <Sheet open={open} onClose={onClose} title={`${stage.sequence_no}. ${stage.name}`}>
      <p className="mb-3 text-sm text-tg-hint">
        Текущий статус: {stageStatusLabel[stage.status]}
        {stage.requires_photo && ' · требуется фото для завершения'}
      </p>

      {commands.length > 0 && (
        <Field label="Комментарий (необязательно)">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
      )}

      <div className="flex flex-col gap-2">
        {commands.map((c) => (
          <Button
            key={c.command}
            fullWidth
            variant={c.variant ?? 'primary'}
            loading={busy}
            onClick={() => run(c.command)}
          >
            {c.label}
          </Button>
        ))}

        {showPause && (
          <div className="rounded-xl border border-black/10 p-3">
            <Field label="Причина паузы *">
              <Select value={delayReasonId} onChange={(e) => setDelayReasonId(e.target.value)}>
                <option value="">— выберите причину —</option>
                {activeReasons.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Button
              fullWidth
              variant="secondary"
              loading={pause.isPending}
              disabled={!delayReasonId}
              onClick={runPause}
            >
              Поставить на паузу
            </Button>
          </div>
        )}

        {isAdmin(user.role) && stage.status !== 'IN_PROGRESS' && (
          <p className="text-xs text-tg-hint">
            Назначение исполнителя и админ-коррекция доступны в расширенном режиме.
          </p>
        )}
      </div>

      {errMessage && (
        <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-risk-red">{errMessage}</p>
      )}
      {commands.length === 0 && !showPause && (
        <p className="text-sm text-tg-hint">Нет доступных действий для этого этапа.</p>
      )}
    </Sheet>
  )
}
